import { Component } from 'react';
import { CssBaseline } from "@material-ui/core";
import CardHeader from '@material-ui/core/CardHeader';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
// Worker CSP de Mapbox. El sufijo ?worker es la forma nativa de Vite de cargarlo
// como Web Worker; sustituye al antiguo `worker-loader!` de webpack.
import MapboxWorker from 'mapbox-gl/dist/mapbox-gl-csp-worker?worker';
import log from 'loglevel';

import { featureTypes } from '../common/Constants';
import {MapService, mapLayers} from "../common/MapService";
import MapData from "../common/MapData";
import CardInfo from "./CardInfo";
import './MapComponent.scss';
import LogManager from "../common/LogManager";
import AppService from "../common/AppService";
import GeoService from "../common/GeoService";
import {ModalComponentService} from "../common/ModalComponentService";
import VisibilityService from "../common/VisibilityService";
import NetworkService from "../common/NetworkService";
import SiteAuth from "../common/SiteAuth";
import type { Logger } from 'loglevel';
import type { LngLat, LngLatBounds, Map, MapboxGeoJSONFeature, Point } from 'mapbox-gl';
import type PlaceData from '../data/PlaceData';
import type MapPosition from '../data/MapPosition';
import type FeatureData from '../data/FeatureData';
import type { DetailCard, CardEventType } from './CardInfo';

export interface MapComponentProps {
    /** El menu abierto tapa la vista de detalle. */
    allowDetail?: boolean;
}

interface MapComponentState {
    init: boolean;
    showDetail: boolean;
    detailData: DetailCard | null;
}

// workerClass no esta en @types/mapbox-gl: es una propiedad del build CSP.
(mapboxgl as unknown as { workerClass: unknown }).workerClass = MapboxWorker;

class MapComponent extends Component<MapComponentProps, MapComponentState> {
    // The map container element.
    mapContainer: HTMLDivElement | null = null;
    // blocks click on touching events on IOS
    isTouchGesture = false;
    touchStartPoint: Point | null = null;
    // Whether the URL feature card has already been loaded (@see showUrlFeature).
    urlFeatureLoaded = false;
    // Whether its load is in flight right now.
    urlFeaturePending = false;

    constructor(props: MapComponentProps) {
        super(props);
        this.logger().debug('MapComponent constructor, props', props);

        this.state = {
            init: true,
            showDetail: false,
            detailData: null,
        }

        this.cancelCardClick = this.cancelCardClick.bind(this);
        this.reloadCardClick = this.reloadCardClick.bind(this);
        this.handleCardEvent = this.handleCardEvent.bind(this);

        this.initialLoading();
    }

    /**
     * Get logger.
     */
    logger(): Logger {
        return log.getLogger('MapComponent');
    }

    /**
     * Get map object.
     *
     */
    getMapObject(): Map {
        return MapService.getMapObject();
    }

    /**
     * Create Map with initial position.
     */
    async initialLoading(): Promise<void> {
        this.logger().debug('initialLoading');
        MapData.init();
    }

    /**
     * Create map after first initial rendering with
     */
    async componentDidMount(): Promise<void> {
        // Create map centered on initial location
        await this.createMap(await MapData.getInitLocation());

        // If the URL points to a feature, trigger its click (loads & shows its
        // card and highlights it on the map).
        const initPlaceData = MapData.getInitPlaceData();

        // Deep link params are only applied when authorized: on the PWA that
        // means authenticated or login not required; on the native app always.
        if (await SiteAuth.checkPwaAuthentication()) {
            // Cold start opened from a deep link (URL feature or coordinates):
            // dismiss any overlay (e.g. the first-run slider) before showing the URL
            // feature. Covers the case where appUrlOpen does not fire on launch.
            if (initPlaceData || GeoService.getLocationFromUrl()) {
                AppService.appcomponent?.closeAllOverlays();
            }

            if (initPlaceData) {
                this.setCardFromPlaceData(initPlaceData, true, true);
            }
        }

        // Refresh hotel/POI marker DOM when visibility toggles (no data reload).
        VisibilityService.addChangeHandler(() => {
            this.refreshVisibilityMarkers();
        });
    }

    /**
     * Show the URL feature card when it could not be loaded before.
     *
     * A deep link can reach the map while the app still has no authorization
     * data, and the card request then fails silently. App calls this once
     * authenticated so the link is not lost (@see App.onAutorize).
     *
     * Does nothing while the map is still being created or while a load is in
     * flight: in those cases the card is already on its way.
     */
    showUrlFeature(): void {
        if (this.urlFeatureLoaded || this.urlFeaturePending
            || this.state.showDetail || !this.getMapObject()) {
            return;
        }

        const placeData = GeoService.getPlaceFromUrl();
        if (placeData) {
            this.logger().debug('Retrying URL feature card', placeData);
            this.setCardFromPlaceData(placeData, true, true);
        }
    }

    /**
     * Apply the URL params to the already-running map. Used when a deep link is
     * received while the app is open (Capacitor 'appUrlOpen', warm start): flies
     * to the URL location and/or highlights the URL feature.
     *
     * The caller must have refreshed UrlParamsService and GeoService caches
     * beforehand (see AppUrlListener).
     */
    async goToUrlParams(): Promise<void> {
        // Any open overlay (modal, page, menu, card) is closed by the caller via
        // App.closeAllOverlays() before this runs (see AppUrlListener).

        const location = GeoService.getLocationFromUrl();
        const placeData = GeoService.getPlaceFromUrl();

        const showFeature = () => {
            if (placeData) {
                // A deep link is programmatic navigation, not a gesture on the
                // map. Clear isTouchGesture in case it got stuck true (e.g. an
                // iOS touch whose touchend never fired because the app was
                // backgrounded), otherwise setCardFromPlaceData would bail out
                // and the card would not open.
                this.isTouchGesture = false;
                this.setCardFromPlaceData(placeData, true, true);
            }
        };

        if (location) {
            const map = this.getMapObject();
            // Keep the fly animation, but defer showing the feature card until it
            // ends ('moveend'): otherwise the card relocation (a panTo based on
            // the current bounds) interrupts the flyTo and the map stops short of
            // the target.
            //
            // We can't rely on a bare once('moveend'): on iOS (WKWebView) the fly
            // animation can be preempted (geolocation, a second deep link) or its
            // moveend dropped, and the card would never open even though the
            // animation runs. So we guard with an idempotent handler plus a
            // fallback timer, and fix the fly duration so the fallback always
            // fires after the animation has settled (never mid-flight, which
            // would reintroduce the interruption above).
            const duration = 1000;
            let shown = false;
            const showOnce = () => {
                if (shown) {
                    return;
                }
                shown = true;
                map.off('moveend', showOnce);
                clearTimeout(fallback);
                showFeature();
            };
            const fallback = setTimeout(showOnce, duration + 400);
            map.on('moveend', showOnce);
            map.flyTo({
                // El geofield y la URL entregan las coordenadas como cadena;
                // mapbox ya las convertia con +valor al construir el LngLat.
                center: [Number(location.longitude), Number(location.latitude)],
                zoom: Number(location.zoom),
                duration,
                essential: true,
            });
        } else {
            showFeature();
        }
    }

    /**
     * Connection restored: if an offline card is currently open, reproduce
     * the original click so the real card loads and replaces it.
     */
    async restoreCardAfterReconnect(): Promise<void> {
        const card = this.state.detailData;
        if (this.state.showDetail && card && card.offline && card.featureData) {
            this.setCardFromPlaceData(card.featureData);
        }
    }

    /**
     * Whether the detail card is currently shown.
     */
    isCardVisible(): boolean {
        return this.state.showDetail;
    }

    /**
     * Hide Card.
     */
    async hideCurrentCard(): Promise<void> {
        if (this.state.showDetail) {
            this.setState({
                showDetail: false,
            });

            // deselect all before select new
            MapData.cleanSelection();

            // Hide possibly hotel / city displayed with other zoom.
            if (!MapData.showHotels) {
                this.cleanHotels(false);
                this.cleanPOIs(false);
            }
            if (!MapData.showPlaces) {
                MapData.setSelectedCity();
            }
        }
    }

    /**
     * Show current card.
     */
    async showCurrentCard(): Promise<void> {
        if (!this.state.showDetail && this.state.detailData) {
            MapService.relocateMapBeforeDetail(this.state.detailData);
            this.setState({
                showDetail: true,
            });
        }
    }


    /**
     * Set new Card Data for display.
     * @param {CardData} cardData
     */
    async setNewCardData(cardData: DetailCard | null): Promise<void> {
        // we need to wait for the new track rendering
        setTimeout(() => {
            if (cardData) MapData.setCurrentCard(cardData);
        }, 1000);
        if (cardData) MapService.relocateMapBeforeDetail(cardData);


        this.setState({
            showDetail: !!cardData,
            detailData: cardData,
        });
    }

    /**
     * Apply the URL feature selection once the map is loaded.
     *
     * The card may resolve before the map has created its layers and sources,
     * so we defer the selection until the map is ready.
     *
     * @param cardData Loaded card for that feature.
     */
    selectUrlFeatureWhenLoaded(placeData: PlaceData, cardData: DetailCard): void {
        if (MapService.mapLoaded) {
            this.selectUrlFeature(placeData, cardData);
        } else {
            this.getMapObject().once('idle', () => {
                this.selectUrlFeature(placeData, cardData);
            });
        }
    }

    /**
     * Highlight on the map the feature referenced by the URL.
     *
     * @param cardData Loaded card for that feature.
     */
    selectUrlFeature(placeData: PlaceData, cardData: DetailCard): void {
        // Cities and hotels / POIs are handled directly (no rendering needed).
        if (MapData.selectUrlFeature(placeData, cardData)) {
            return;
        }
        // Routes / stages need their rendered geometry; wait for the map to settle.
        this.selectUrlFeatureWhenReady(placeData);
    }

    /**
     * Reproduce on the map the selection of the feature referenced by the URL.
     *
     * The map needs to fly to the feature and render it before we can query it,
     * so we try and retry on a short timer until the feature is found. Polling
     * (instead of waiting for 'idle') guarantees the retries always terminate
     * and the failure is logged, even when the map stops moving.
     *
     * @param attempts Remaining attempts.
     */
    selectUrlFeatureWhenReady(placeData: PlaceData, attempts = 5): void {
        if (MapData.selectRenderedFeature(placeData.type, placeData.id)) {
            return;
        }
        if (attempts <= 0) {
            this.logger().debug('URL feature not rendered for selection', placeData);
            return;
        }
        setTimeout(() => this.selectUrlFeatureWhenReady(placeData, attempts - 1), 500);
    }

    /**
     * Set card from place data if possible.
     * @param hideCurrent Hide current card before, depending on transition wanted
     * @param fromUrl Whether the feature comes from the URL (it lacks a
     *   rendered map feature, so it is highlighted from the loaded card instead).
     */
    async setCardFromPlaceData(placeData: FeatureData, hideCurrent = true, fromUrl = false): Promise<void> {
        if (this.isTouchGesture)
            return;

        this.logger().debug('Set Card from Place', placeData);
        if (hideCurrent) this.hideCurrentCard();
        LogManager.closeLog();
        if (fromUrl) {
            this.urlFeaturePending = true;
        }
        MapData.getCardFromPlaceData(placeData as PlaceData)
            .then((cardData) => {
                if (fromUrl) {
                    this.urlFeaturePending = false;
                    this.urlFeatureLoaded = !!cardData;
                }
                if (!cardData)
                    return;
                cardData.clickPoint = placeData.clickPoint;
                // Deep links carry no click point, so the "move the feature out
                // from under the card" relocation (only forced when a click point
                // exists) never fired: the map is centered exactly on the feature,
                // leaving it dead-centered and half covered by the card. Reuse that
                // forced relocation by treating the feature's own location as the
                // click point.
                if (fromUrl && !cardData.clickPoint && cardData.getGeoLocation()) {
                    cardData.clickPoint = cardData.getGeoLocation();
                }
                if (cardData.subtitle) {
                    const trackNumber = cardData.subtitle.split(' ')[1];
                    cardData.title = `${trackNumber}: ${cardData.data!.title}`;
                } else {
                    cardData.title = `${cardData.title}`;
                }

                this.setNewCardData(cardData);

                if (fromUrl) {
                    this.selectUrlFeatureWhenLoaded(placeData as PlaceData, cardData);
                } else {
                    MapData.setSelectedPlace(placeData);
                }
            });
    }

    /**
     * clean all accommodations and redraw.
     *
     * @param {boolean} keep_selected Whether to keep selected one.
     */
    cleanHotels(keep_selected = true): void {
        this.logger().debug("cleanHotels keep_selected", keep_selected);
        MapData.cleanHotelMarkers(keep_selected);
        // It seems we need to delete from document too.
        this.cleanDocumentHotels(keep_selected);
    }

    /**
     * clean all hotel markers querying the document.
     *
     * @param keep_selected Whether to keep selected one.
     */
    cleanDocumentHotels(keep_selected = true): void {
        // remove all but selected
        const queryClass = '.accommodation' + (keep_selected ? ':not(.selected)' : '');
        const hotels =
            document.querySelectorAll('.mapboxgl-canvas-container.mapboxgl-interactive ' + queryClass);

        hotels.forEach((marker) => {
            marker.remove();
        });
    }

    /**
     * Reload / clean hotels, depending on zoom level.
     */
    async reloadHotelsAndPOIs(bounds: LngLatBounds): Promise<void> {
        if (!MapData.showHotels) {
            // Limpiar aunque estemos offline: el zoom ya no permite ver alojamientos/POIs.
            this.cleanHotels(false);
            this.cleanPOIs(false);
            return;
        }

        if (NetworkService.isOffline()) {
            return;
        }

        const [hotelList, POIList] = await MapData.getHotelsAndPOIsForBounds(bounds);
        this.cleanHotels(false);
        this.cleanPOIs(false);
        this.showPOIList(POIList);
        this.showHotelList(hotelList);
    }

    /**
     * Show new hotel list, add click events, etc...
     *
     */
    async showHotelList(hotelList: { getDataItems: () => PlaceData[] }): Promise<void> {
        const map = this.getMapObject();
        hotelList.getDataItems().forEach((item: PlaceData) => {
            const el = item.createElement();
            el.addEventListener(AppService.getInteractiveEventType(), (event) => {
                this.handleHotelClick(item, event as MouseEvent);
            });
            item.addToMap(map);
        });
    }

    /**
     * Show new POI list, add click events, etc...
     *
     */
    async showPOIList(POIList: { getDataItems: () => PlaceData[] }): Promise<void> {
        const map = this.getMapObject();
        POIList.getDataItems().forEach((item: PlaceData) => {
            const el = item.createElement();
            el.addEventListener(AppService.getInteractiveEventType(), (event) => {
                this.handleHotelClick(item, event as MouseEvent);
            });
            item.addToMap(map);
        });
    }

    /**
     * clean all accommodations and redraw.
     *
     * @param {boolean} keep_selected Whether to keep selected one.
     */
    cleanPOIs(keep_selected = true) {
        this.logger().debug("cleanPOIs keep_selected", keep_selected);
        MapData.cleanPOIMarkers(keep_selected);
        // It seems we need to delete from document too.
        this.cleanDocumentPOIs(keep_selected);
    }



    /**
     * clean all POI markers querying the document.
     *
     * @param {boolean} keep_selected Whether to keep selected one.
     */
    cleanDocumentPOIs(keep_selected = true) {
        // remove all but selected
        const queryClass = '.poi' + (keep_selected ? ':not(.selected)' : '');
        const POIs =
            document.querySelectorAll('.mapboxgl-canvas-container.mapboxgl-interactive ' + queryClass);

        POIs.forEach((marker) => {
            marker.remove();
        });
    }

    /**
     * Refresh hotel/POI marker DOM elements based on VisibilityService.
     * No data reload; just re-runs createElement() on existing items so they
     * can update className/innerHTML.
     */
    refreshVisibilityMarkers(): void {
        if (!this.getMapObject()) return;
        if (!MapData.showHotels) return; // if zoom hides them, do nothing

        try {
            MapData.hotelList?.getDataItems?.().forEach((item: PlaceData) => item.createElement());
            MapData.POIList?.getDataItems?.().forEach((item: PlaceData) => item.createElement());
        } catch {
            // noop
        }
    }

    /*
     * Create map object, add event listenersHelper functions
     *
     */
    async createMap(initLocation: { longitude: string | number; latitude: string | number; zoom?: string | number }): Promise<void> {
        this.logger().debug('createMap');

        // La URL entrega las coordenadas como cadena; mapbox ya las convertia
        // con +valor al construir el LngLat.
        const mapCenter: [number, number] = [Number(initLocation.longitude), Number(initLocation.latitude)];
        const mapZoom = initLocation.zoom;

        // El zoom llega como cadena desde la URL; createMap espera un numero.
        const map = MapService.createMap(this, this.mapContainer!, mapCenter, mapZoom === undefined ? undefined : Number(mapZoom));

        // More stuff when map loaded.
        // The city list is not loaded from here: App.loadCityList takes care of
        // it on every start, so loading it again when the map opens at the
        // initial zoom only meant downloading the same records twice.
        map.on('load', async () => {
            await this.mapEventLoad(map);
            await MapService.mapEventLoad();
            MapService.updateMapComponent();
        });

        map.on('idle', async () => {
            MapService.createTrackNumbers();
            MapService.createRouteNames();
        })

        this.mapContainer!.addEventListener(AppService.getInteractiveEventType(), () => {
            if (this.isTouchGesture) return;

            if (this.state.showDetail) {
                this.hideCurrentCard();
            } else {
                MapData.cleanSelection();
            }
        });

        if (AppService.isIos() && AppService.isNativeApp()) {
            map.on('touchstart', (e: mapboxgl.MapTouchEvent) => {
                const touches = e.originalEvent.touches;
                this.isTouchGesture = touches.length > 1;
                if (touches.length === 1) {
                    this.touchStartPoint = e.point;
                }
            });

            map.on('touchmove', (e: mapboxgl.MapTouchEvent) => {
                const endPoint = e.point;
                if (
                    Math.abs(this.touchStartPoint!.x - endPoint.x) > 10 ||
                    Math.abs(this.touchStartPoint!.y - endPoint.y) > 10
                ) {
                    this.isTouchGesture = true;
                } else {
                    this.isTouchGesture = false;
                }
            });

            map.on('touchend', () => {
                setTimeout(() => {
                    this.isTouchGesture = false;
                }, 50);
            });
        }
    }


    /**
     * Create map layers events.
     */
    async createMapLayersEvents(map: Map): Promise<void> {
        // Interactive places
        map.on('mouseenter', mapLayers.interactiveCities, () => {
            map.getCanvas().style.cursor = 'pointer';
        });

        map.on('mouseleave', mapLayers.interactiveCities, () => {
            map.getCanvas().style.cursor = '';
        });
        map.on(AppService.getInteractiveEventType(), mapLayers.interactiveCities, (event) => {
            this.mapEventFeatureClick(event, event.features![0]);
        });

        // Interactive tracks
        map.on('mouseenter', mapLayers.interactiveTracks, () => {
            map.getCanvas().style.cursor = 'pointer';
        });

        map.on('mouseleave', mapLayers.interactiveTracks, () =>{
            map.getCanvas().style.cursor = '';
        });

        if (AppService.isIos() && AppService.isNativeApp()) {
            let startPoint: Point | undefined;
            map.on('touchstart', mapLayers.interactiveTracks, (event) => {
                startPoint = event.point;
            });
            map.on('touchend', mapLayers.interactiveTracks, (event) => {
                const endPoint = event.point;
                if (Math.abs(startPoint!.x - endPoint.x) < 10 && Math.abs(startPoint!.y - endPoint.y) < 10) {
                    this.mapEventFeatureClick(event, event.features![0]);
                }
            });
        } else {
            map.on(AppService.getInteractiveEventType(), mapLayers.interactiveTracks, (event) => {
                this.mapEventFeatureClick(event, event.features![0]);
            });
        }

    }

    // Map load event.
    async mapEventLoad(map: Map): Promise<void> {
        this.logger().debug("Component: mapEventLoad");
        if (this.state.init) {
            this.setState({
                init: false,
                // showDetail: false,
            });

            // Add controls, layers and buttons
            await MapService.createMapControls(map);
            await MapService.createMapLayers(map);

            this.createMapLayersEvents(map);

            // This will take care of layer visibility too.
            MapService.setMapLoaded();
        }
    }

    /**
     * Map mixed event. Invoked from MapService
     */
    async mapEventChanged(mapPos: MapPosition): Promise<void> {
        const {center, zoom, bounds} = mapPos;
        this.logger().debug("mapEventChanged (zoom, center)", zoom, center);
        MapData.setVisibility(zoom!);
        await this.reloadHotelsAndPOIs(bounds!);
        MapData.showSelected();
        if (ModalComponentService.modalComponentType === ModalComponentService.ModalComponentTypes.OUT_OF_MAP_CARD) {
            ModalComponentService.reset();
        }
        LogManager.closeLog();
    }

    /**
     * The current stage button has been clicked.
     */
    async mapEventClickStageControl(): Promise<void> {
        if (MapData.selectedCard) {
            await MapService.zoomToStageLevel();
            this.setNewCardData(MapData.selectedCard);
        }
    }

    /**
     * Clicked map feature, handle event.
     */
    async mapEventFeatureClick(event: mapboxgl.MapLayerMouseEvent | mapboxgl.MapLayerTouchEvent, feature: mapboxgl.MapboxGeoJSONFeature): Promise<void> {
        this.logger().debug('mapEventFeatureClick', event);

        // Some event handling, grab here.
        if (event && event.originalEvent) {
            event.originalEvent.stopImmediatePropagation();
        }

        // const feature = event.features[0];
        const layer = feature.layer;
        let type;

        // The event layer will tell us the feature type.
        switch (layer.id) {
            case mapLayers.interactiveCities:
                type = featureTypes.localidad;
                break;
            case mapLayers.interactiveTracks:
                type = featureTypes.track;
                break;
            default:
            // We don't know this layer'
        }

        if (type) {
            this.handleFeatureClick(type, feature, event.lngLat);
        }
        else {
            this.logger().warn('Unknown map layer from feature click', layer, event);
        }
    }

    /**
     * Clicked map feature, handle event.
     *
     * @param type Feature type
     * @param feature Map feature
     */
    async handleFeatureClick(type: string, feature: unknown, point: unknown): Promise<void> {
        this.logger().debug('handleFeatureClick, type ' + type, feature);
        const placeData = MapData.getSelectedFeatureData(type, feature as MapboxGeoJSONFeature);
        if (placeData) {
            placeData.clickPoint = point as LngLat;
            this.setCardFromPlaceData(placeData);
        }
    }

    /**
     * Clicked hotel, handle event.
     */
    async handleHotelClick(placeData: PlaceData, event: MouseEvent): Promise<void> {
        event.preventDefault();
        event.stopPropagation();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();
        this.setCardFromPlaceData(placeData);
    }

    /**
     * Handle card click events: select, prev, next
     *
     */
    async handleCardEvent(eventType: CardEventType, card: DetailCard): Promise<void> {
        let newPlace;

        switch (eventType) {

            case CardInfo.EVENT_TYPE_SELECT:
                log.debug("Selected card", card);
                MapData.setSelectedCard(card);
                break;

            case CardInfo.EVENT_TYPE_NEXT:
                log.debug("Next card", card);
                newPlace = card.getNext();
                break;

            case CardInfo.EVENT_TYPE_PREV:
                log.debug("Prev card", card);
                newPlace = card.getPrev();
                break;

            default:
                log.error("Unknown card event type", eventType);
        }

        if (newPlace) {
            log.debug("Switching to new feature", newPlace);
            this.setCardFromPlaceData(newPlace, false);
        }
    }

    /**
     * Reload last loaded card data.
     */
    reloadCardClick(): void {
        this.hideCurrentCard();
        if (MapData.hasCardLoaded()) {
            this.showCurrentCard();
        }
    }

    /**
     * Event: Click cancel button on card.
     */
    cancelCardClick(): void {
        this.hideCurrentCard();
    }

    /**
     * Gets renderable card detail content.
     *
     * @param {CardData} cardData
     */
    getDetailInfo(cardData: DetailCard | null) {
        if (!this.props.allowDetail) return;

        const cardEvents = {
            cancelClick: this.cancelCardClick,
            reloadClick: this.reloadCardClick,
            handleCardEvent: this.handleCardEvent,
        };
        if (cardData && cardData.properties){
            const detailTitle = cardData.getTitle();
            const detailLink = cardData.getUrl();
            const disabledClass = detailLink === undefined ? 'disabled' : '';

            return <CardInfo
                title={detailTitle}
                disabledClass={'link-localidad ' + disabledClass}
                cardData={cardData}
                cardEvents={cardEvents}
                show={this.state.showDetail ? 'true' : 'false'}
            > </CardInfo>
        } else {
            // Fall back. Return empty card just in case.
            return <CardInfo
                title="Empty card"
                show={'false'}
            >
                <CardHeader title="Empty Card Content" />
            </CardInfo>
        }
    }

    /**
     * Used state properties:
     * -init
     * -showDetail
     * -detailData
     */
    render() {
        // this.logger().debug('Map.render', this.state);

        // to control detail view visibility
        document.getElementById('app-container')!
            .classList.toggle('detailView', this.state.showDetail && !!this.props.allowDetail);

        return (
            // themeprovider to get material customization styles
            <div className='mapComponent'
                 onClick={() => {
                     if (ModalComponentService.modalComponentType === ModalComponentService.ModalComponentTypes.OUT_OF_MAP_CARD) {
                         ModalComponentService.reset();
                     }
                 }}>
                <CssBaseline />
                <div ref={el => this.mapContainer = el} className="absolute top right left bottom" />

                {
                    this.getDetailInfo(this.state.detailData)
                }
            </div>
        );
    }
}

export default MapComponent;
